import { useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import { Button1 } from "../index";
import { transfersApi } from "../../api/endpoints";

const BRAND_GREEN = "#1BBB6B";

const ERROR_MESSAGES = {
  OTP_REQUIRED: "Please enter the 6-digit code we emailed you.",
  OTP_INVALID: "That code is incorrect. Please try again.",
  OTP_EXPIRED: "That code has expired. Request a new one.",
  OTP_MAX_ATTEMPTS: "Too many wrong attempts. Request a new code.",
  OTP_RESEND_TOO_SOON: "Please wait a moment before requesting another code.",
  PIN_REQUIRED: "Please enter your 6-digit transaction PIN.",
  PIN_INVALID: "That PIN is incorrect. Please try again.",
  PIN_LOCKED: "Too many wrong PIN attempts. Try again later, or switch to email OTP in Settings.",
  PIN_NOT_SET: "No PIN is set on your account. Set one in Settings.",
};

const CodeBox = ({ label, value, onChange, onEnter }) => (
  <div className="d-flex flex-column gap-2">
    <label className="fs12px" style={{ color: "#7D7D7D" }}>{label}</label>
    <input
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
      onKeyDown={(e) => e.key === "Enter" && onEnter()}
      placeholder="______"
      className="form-control text-center fw-bold"
      style={{ fontSize: 24, letterSpacing: 10, padding: "10px 8px", borderColor: BRAND_GREEN }}
    />
  </div>
);

/**
 * Verification step for outbound transfers. Which code(s) it asks for
 * depends on the user's saved transactionVerificationMode (Settings >
 * Security > Transaction Verification):
 *   OTP , emails a 6-digit code, shows one box
 *   PIN , nothing is emailed, shows the user's own saved PIN box
 *   BOTH, emails a code AND shows the PIN box, both required
 * onVerify({ otpCode, pinCode }) performs the actual transfer create.
 */
const TransferOtpModal = ({ open, onClose, onVerify }) => {
  const [mode, setMode] = useState("OTP");
  const [otpCode, setOtpCode] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const requestedRef = useRef(false);

  const requestCode = async () => {
    setError(null);
    try {
      const res = await transfersApi.requestOtp();
      setMode(res.mode || "OTP");
      if (res.sent === false) {
        setInfo("Enter your transaction PIN below.");
      } else {
        setInfo(
          res.channel === "email+sms"
            ? "We've sent a 6-digit code to your email and phone."
            : "We've sent a 6-digit code to your email."
        );
        setCooldown(res.resendAfterSeconds || 60);
      }
    } catch (err) {
      const apiCode = err.response?.data?.error;
      if (apiCode === "OTP_RESEND_TOO_SOON") {
        setInfo("A code was already sent, check your email.");
        setCooldown(30);
      } else {
        setError(ERROR_MESSAGES[apiCode] || "Could not send the code. Try again.");
      }
    }
  };

  useEffect(() => {
    if (open && !requestedRef.current) {
      requestedRef.current = true;
      requestCode();
    }
    if (!open) {
      requestedRef.current = false;
      setOtpCode("");
      setPinCode("");
      setError(null);
      setInfo(null);
      setCooldown(0);
      setMode("OTP");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const needsOtp = mode === "OTP" || mode === "BOTH";
  const needsPin = mode === "PIN" || mode === "BOTH";
  const ready = (!needsOtp || otpCode.length === 6) && (!needsPin || pinCode.length === 6);

  const handleVerify = async () => {
    if (verifying) return;
    if (needsOtp && otpCode.length !== 6) {
      setError(ERROR_MESSAGES.OTP_REQUIRED);
      return;
    }
    if (needsPin && pinCode.length !== 6) {
      setError(ERROR_MESSAGES.PIN_REQUIRED);
      return;
    }
    setError(null);
    setVerifying(true);
    try {
      await onVerify({ otpCode: needsOtp ? otpCode.trim() : undefined, pinCode: needsPin ? pinCode.trim() : undefined });
    } catch (err) {
      const apiCode = err.response?.data?.error;
      setError(ERROR_MESSAGES[apiCode] || apiCode || err.message || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={420} closable>
      <div className="d-flex flex-column gap-3 p-2">
        <span className="fs-5 fw-semibold">Verify your transfer</span>
        <span className="fs12px" style={{ color: "#7D7D7D" }}>
          {info || "Preparing verification..."}
        </span>

        {needsOtp && (
          <CodeBox label="Email code" value={otpCode} onChange={setOtpCode} onEnter={handleVerify} />
        )}
        {needsPin && (
          <CodeBox label="Transaction PIN" value={pinCode} onChange={setPinCode} onEnter={handleVerify} />
        )}

        {error && (
          <span className="fs12px" style={{ color: "#FC555B" }}>
            {error}
          </span>
        )}

        <div className="d-flex align-items-center justify-content-between">
          {needsOtp ? (
            <span
              className={`fs12px ${cooldown > 0 ? "" : "cursor-pointer"}`}
              style={{ color: cooldown > 0 ? "#7D7D7D" : BRAND_GREEN }}
              onClick={() => cooldown <= 0 && requestCode()}
            >
              {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
            </span>
          ) : (
            <span />
          )}
          <Button1
            buttonName={verifying ? "Verifying..." : "Verify & Send"}
            className="btn-dark"
            onClick={handleVerify}
            disabled={verifying || !ready}
          />
        </div>
      </div>
    </Modal>
  );
};

export default TransferOtpModal;
